import React, { CSSProperties, useEffect, useRef, useState } from "react";

export type HeadroomProps = {
  /** Callback when header is pinned (shown). */
  onPin?: () => void;

  /** Callback when header is unpinned (hidden). */
  onUnpin?: () => void;

  /** Scroll position (in pixels) where pinning behavior starts. */
  pinStart?: number;

  /** Custom className for the wrapper. */
  className?: string;

  /** Custom inline styles for the wrapper. */
  style?: CSSProperties;

  /** ID attribute for the wrapper element. */
  id?: string;

  /**
   * Scroll tolerance in pixels before triggering pin/unpin
   *
   * @default 5
   */
  tolerance?: number;

  /**
   * Disable the headroom behavior
   *
   * @default false
   */
  disable?: boolean;

  /** Children to render inside the headroom. */
  children?: React.ReactNode;
};

/**
 * Custom Headroom component that shows/hides based on scroll direction
 * Replacement for react-headroom with modern React patterns
 */
export const Headroom = ({
  onPin,
  onUnpin,
  pinStart = 0,
  className = "",
  style = {},
  id,
  tolerance = 5,
  disable = false,
  children,
}: HeadroomProps) => {
  const [state, setState] = useState<"unfixed" | "pinned" | "unpinned">(
    "unfixed"
  );
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const lastScrollY = useRef(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  /** Measure and set the height of the inner content. */
  useEffect(() => {
    const measureHeight = () => {
      if (innerRef.current) {
        // getBoundingClientRect keeps the sub-pixel height; offsetHeight rounds to an
        // integer, leaving a ~1px gap below the fixed header that shows as a white seam.
        setHeight(innerRef.current.getBoundingClientRect().height);
      }
    };

    measureHeight();

    /**
     * Remeasure on content size changes to handle async-rendered content
     * and prevent header overlap.
     */
    const observer = innerRef.current
      ? new ResizeObserver(measureHeight)
      : undefined;

    if (innerRef.current && observer) {
      observer.observe(innerRef.current);
    }

    /** Remeasure on window resize. */
    window.addEventListener("resize", measureHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measureHeight);
    };
  }, [children]);

  /** Handle scroll behavior. */
  useEffect(() => {
    if (disable) {
      return;
    }

    let ticking = false;

    const handleScroll = () => {
      if (ticking) {
        return;
      }

      ticking = true;

      globalThis.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const scrollDifference = currentScrollY - lastScrollY.current;

        /** At or above pinStart - unfix (position: relative). */
        if (currentScrollY <= pinStart) {
          if (state !== "unfixed") {
            setState("unfixed");
            onPin?.();
          }

          lastScrollY.current = currentScrollY;
          ticking = false;

          return;
        }

        /** Calculate total scroll threshold (banner + header). */
        const scrollThreshold = pinStart + (height || 0);

        /** Still scrolling naturally - header not fully out of view yet. */
        if (state === "unfixed" && currentScrollY < scrollThreshold) {
          lastScrollY.current = currentScrollY;
          ticking = false;

          return;
        }

        /** Header fully scrolled past - switch to fixed and hidden (no animation). */
        if (state === "unfixed" && currentScrollY >= scrollThreshold) {
          setState("unpinned");
          setShouldAnimate(false);
          onUnpin?.();

          lastScrollY.current = currentScrollY;
          ticking = false;

          return;
        }

        /** Scrolling down - hide header. */
        if (scrollDifference > tolerance && state === "pinned") {
          setState("unpinned");
          setShouldAnimate(true);
          onUnpin?.();
        }

        /** Scrolling up - show header. */
        if (scrollDifference < -tolerance && state === "unpinned") {
          setState("pinned");
          setShouldAnimate(true);
          onPin?.();
        }

        lastScrollY.current = currentScrollY;
        ticking = false;
      });
    };

    /** Initialize scroll position. */
    lastScrollY.current = window.scrollY;

    /** Add scroll listener with passive flag for better performance. */
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [state, onPin, onUnpin, pinStart, tolerance, disable, height]);

  /**
   * Calculate transform based on state. Use `none` (not `translateY(0)`) when shown
   * so the header isn't promoted to its own GPU layer at rest — a composited fixed
   * layer anti-aliases its bottom edge against the page and shows a thin white seam.
   */
  const transform = state === "unpinned" ? "translateY(-100%)" : "none";

  /** Wrapper style - maintains space in the document flow. */
  const wrapperStyle: CSSProperties = {
    height: height ? `${height}px` : undefined,
  };

  /** Inner style - fixed positioning with transform. */
  const innerStyle: CSSProperties = {
    position: disable || state === "unfixed" ? "relative" : "fixed",
    top: 0,
    left: 0,
    right: 0,
    transform,
    transition: shouldAnimate ? "transform 200ms ease-in-out" : "none",
    // only promote to a layer while animating; at rest it must paint with the page to avoid the seam
    willChange: shouldAnimate ? "transform" : "auto",
    ...style,
  };

  const wrapperClassName = className
    ? `${className} headroom-wrapper`
    : "headroom-wrapper";

  return (
    <div
      ref={wrapperRef}
      id={id}
      className={wrapperClassName}
      style={wrapperStyle}
    >
      <div
        ref={innerRef}
        style={innerStyle}
        onTransitionEnd={() => setShouldAnimate(false)}
      >
        {children}
      </div>
    </div>
  );
};
